<template>
  <div v-if="!isVoiceRecording" class="w-100">
    <div
      class="octadesk-chat-message-wrapper"
      :class="{ 'message-active': isActive }"
    >
      <NumberInput
        v-if="showNumberInput"
        class="space-right-sm"
        :disabled="getUploadInProcess"
        @input="handleUpdateNumberInput"
        @enterKeypress="handleEnterKeypress"
      />

      <DatePicker
        v-else-if="showDatePicker"
        :disabled="getUploadInProcess"
        @input="handleUpdateDatePicker"
        @enterKeypress="handleUpdateDate"
      />

      <textarea
        v-else
        id="octadesk--chat--comment"
        ref="comment"
        v-model="computedComment"
        v-octa-typing
        :disabled="isDisabled"
        class="octadesk-chat-message-textarea"
        :class="{ 'message--typing--color': comment.length }"
        :placeholder="placeholder"
        rows="1"
        @keydown="updateActivity()"
        @keyup="autoExpandTextarea()"
        @keypress.enter="handleSendMessage($event)"
        @keypress="handleKeypress"
        @focus="setActive()"
        @blur="resetMessageField()"
      ></textarea>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'

  import { mapGetters, mapActions } from 'vuex'

  import { isClient, pasteImage } from '@/helpers'

  import NumberInput from '@/app/shared-components/NumberInput'

  import DatePicker from '@/app/shared-components/DatePicker'

  export default {
    components: {
      NumberInput,
      DatePicker
    },
    props: {
      getFooterHeight: {
        type: Function,
        required: true
      },
      sending: {
        type: Boolean,
        required: true
      },
      handleSendMessage: {
        type: Function,
        required: true
      }
    },
    data() {
      return {
        isActive: false,
        isDesktop: window.innerWidth > 768,
        pasteImageInstance: undefined
      }
    },
    computed: {
      ...mapGetters([
        'commentError',
        'comment',
        'emojiPageStatus',
        'isVoiceRecording',
        'chat',
        'disableUserInput',
        'showNumberInput',
        'showDatePicker',
        'getUploadInProcess',
        'botHasFinished'
      ]),
      computedComment: {
        get() {
          return this.comment || ''
        },
        set(value) {
          const sanitizedValue = value.replace(/[\u{1D4EA}-\u{1D503}]/gu, '')
          const limitedValue = sanitizedValue.slice(0, 1000)
          this.$refs.comment.value = limitedValue
          if (limitedValue.length === 1000)
            this.updateCommentErrorMaxLength(true)
          else if (this.commentError) this.updateCommentErrorMaxLength(false)
          this.updateComment(limitedValue)
        }
      },
      baseScrollHeight() {
        return this.$refs.comment.scrollHeight
      },
      currentLastMessage() {
        return (
          this.chat &&
          this.chat.messages &&
          this.chat.messages.length &&
          this.chat.messages[this.chat.messages.length - 1]
        )
      },
      requestPhoneMessage() {
        return (
          this.botProperty &&
          (this.botProperty.type === 'phone' ||
            this.botProperty.type.includes('phoneContact.'))
        )
      },
      requestDateMessage() {
        return (
          this.botProperty &&
          (this.botProperty.type === 'date' ||
            this.botProperty.type.includes('dateContact.'))
        )
      },
      botExtendedData() {
        const bot =
          this.currentLastMessage &&
          this.currentLastMessage.extendedData &&
          this.currentLastMessage.extendedData.bot
        return bot
      },
      botProperty() {
        return this.botExtendedData && this.botExtendedData.property
      },
      botPlaceholder() {
        const lastmessage = this.currentlastmessage

        let placeholder =
          lastmessage &&
          lastmessage.extendedData &&
          lastmessage.extendedData.bot &&
          lastmessage.extendedData.bot.placeholder

        return placeholder
      },
      requestOnlyNumbers() {
        return (
          this.botProperty &&
          (this.botProperty.type === 'number' ||
            this.botProperty.type === 'float')
        )
      },
      requestEmail() {
        return this.botProperty && this.botProperty.type === 'email'
      },
      requestOptionsSelection() {
        const buttons =
          this.currentLastMessage &&
          this.currentLastMessage.extendedData &&
          this.currentLastMessage.extendedData.bot &&
          this.currentLastMessage.extendedData.bot.buttons

        return buttons && buttons.length
      },
      placeholder() {
        if (!this.isDisabled) {
          const lastmessage = this.currentLastMessage

          if (lastmessage && !isClient(lastmessage.user.type)) {
            if (this.botPlaceholder && this.botPlaceholder.length) {
              return this.botPlaceholder
            } else if (this.requestEmail) {
              return this.$t('conversation.footer.comment.replyWithAnEmail')
            } else if (this.requestOnlyNumbers) {
              return this.$t('conversation.footer.comment.replyNumber')
            } else if (this.requestOptionsSelection) {
              return this.$t(
                'conversation.footer.comment.replyOptionsSelection'
              )
            } else {
              return this.$t('conversation.footer.comment.replyToMessage')
            }
          } else {
            return this.$t('conversation.footer.comment.sendAMessage')
          }
        } else {
          if (this.requestOptionsSelection) {
            return this.$t('conversation.footer.comment.replyOptionsSelection')
          } else {
            return ''
          }
        }
      },
      isDisabled() {
        return (
          this.getUploadInProcess ||
          this.sending ||
          (this.isVoiceRecording && !this.computedComment) ||
          (this.disableUserInput && !this.botHasFinished)
        )
      }
    },
    watch: {
      requestPhoneMessage(value) {
        this.setShowNumberInput(!!value)
      },
      currentLastMessage() {
        if (this.requestPhoneMessage) {
          this.setShowNumberInput(true)

          return
        }

        if (this.requestDateMessage) {
          this.setShowDatePicker(true)

          return
        }

        this.$refs.comment && this.$refs.comment.focus()
      }
    },
    mounted() {
      this.bindImagePasteBehaviour()

      if (this.requestPhoneMessage) {
        this.setShowNumberInput(true)
      }
      if (this.requestDateMessage) {
        this.setShowDatePicker(true)
      }
    },
    beforeDestroy() {
      this.pasteImageInstance && this.pasteImageInstance.destroy()

      if (this.requestPhoneMessage) {
        this.setShowNumberInput(false)
      }
      if (this.requestDateMessage) {
        this.setShowDatePicker(false)
      }
    },
    methods: {
      ...mapActions([
        'updateActivity',
        'updateComment',
        'updateCommentErrorMaxLength',
        'setShowNumberInput',
        'setShowDatePicker',
        'setNumberInputValid'
      ]),
      handleKeypress($event) {
        if (this.requestOnlyNumbers && !this.isNumber($event)) {
          $event.preventDefault()
        }
        return true
      },
      handleEnterKeypress({ isValid }) {
        if (isValid) {
          this.handleSendMessage()
        }
      },
      autoExpandTextarea() {
        if (this.$refs.comment) {
          let minRows = 1

          let maxRows = 10

          let computed

          let oldCount = this.$refs.comment.rows

          this.$refs.comment.rows = minRows

          let rows = Math.ceil(
            (this.$refs.comment.scrollHeight - this.baseScrollHeight) / 23
          )

          computed = minRows + rows < maxRows ? minRows + rows : maxRows

          if (computed !== oldCount) {
            this.getFooterHeight()
          }

          this.$refs.comment.rows = computed

          this.$emit('expand', computed)
        }
      },
      setActive() {
        if (!this.isDesktop && this.emojiPageStatus) {
          this.disableEmojiPage()
        }
        this.isActive = true
      },
      resetMessageField() {
        if (this.comment === '') {
          this.isActive = false
        }
      },
      handleUpdateNumberInput(updatedValue) {
        if (updatedValue && updatedValue.isValid) {
          this.setNumberInputValid(updatedValue.isValid)

          this.updateComment(updatedValue.formattedNumber)
        } else {
          this.setNumberInputValid(updatedValue.isValid)
        }
      },
      isNumber(evt) {
        evt = evt || window.event

        var charCode = evt.which ? evt.which : evt.keyCode

        if (
          charCode > 31 &&
          (charCode < 48 || charCode > 57) &&
          charCode !== 46
        ) {
          evt.preventDefault()
        } else {
          return true
        }
      },
      handleUpdateDatePicker(updatedValue) {
        this.updateComment(updatedValue)
      },
      handleUpdateDate(date) {
        this.updateComment(date)

        this.handleSendMessage()
      },
      bindImagePasteBehaviour() {
        this.pasteImageInstance = new pasteImage(this.$el)

        this.pasteImageInstance.on('paste-image', ({ blob }) => {
          if (Vue.prototype.$dropzone_ref) {
            Vue.prototype.$dropzone_ref.addFile(blob)
          } else {
            console.error(
              'Error on upload clipboard image: Missing dropzone reference'
            )
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .w-100 {
    width: 100%;
  }

  .octadesk-chat {
    &-message-wrapper {
      font-family: Helvetica, sans-serif !important;
      color: #5a5a5a;
      font-size: 0.875rem;
    }

    &-message-textarea {
      margin: 0 auto;
      padding: 0;
      border: none;
      resize: none;
      outline: none;
      overflow-x: hidden;
      overflow-y: auto;
      -webkit-font-smoothing: antialiased;
      min-height: 16px;
    }
  }

  .message--typing--color {
    color: #292929;
  }
</style>
